/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// src/components/ManageUsers.js
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Form,
  Spinner,
  Alert,
  Table,
  Accordion,
  Button,
  Dropdown,
  Modal,
  Nav,
  Card,
  Badge
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DriverProfileAdminModal from './DriverProfileAdminModal';

export default function ManageUsers() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'sms-templates'

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const token = localStorage.getItem('token');

  // Data state
  const [users, setUsers]             = useState([]);
  const [stats, setStats]             = useState({});  // { [email]: { accepted, declined, passed, completed } }
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [searchTerm, setSearchTerm]   = useState('');
  const [sortConfig, setSortConfig]   = useState({ key: 'name', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'suspended', 'pending'

  // Action modals state
  const [editUser, setEditUser]       = useState(null);

  const [deleteUser, setDeleteUser]   = useState(null);
  const [deleting, setDeleting]       = useState(false);

  // SMS Templates state
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [showSmsTemplateModal, setShowSmsTemplateModal] = useState(false);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState(null);
  const [smsTemplateForm, setSmsTemplateForm] = useState({
    name: '',
    title: '',
    message: '',
    isActive: true,
    order: 0
  });
  const [savingSmsTemplate, setSavingSmsTemplate] = useState(false);

  // Fetch users + stats
  useEffect(() => {
    (async () => {
      try {
        const [uRes, sRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/user-stats`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        // Sort users by latest first (by createdAt)
        const sortedUsers = Array.isArray(uRes.data) 
          ? uRes.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          : [];
        setUsers(sortedUsers);

        // Normalize stats response into array
        const statsArray = Array.isArray(sRes.data)
          ? sRes.data
          : Array.isArray(sRes.data.stats)
            ? sRes.data.stats
            : [];

        const lookup = {};
        statsArray.forEach(u => {
          lookup[u.email] = {
            accepted:  u.acceptedCount   || 0,
            declined:  u.declinedCount   || 0,
            passed:    u.passedCount     || 0,
            completed: u.completedCount  || 0
          };
        });
        setStats(lookup);
      } catch (err) {
        console.error(err);
        setError('Failed to load users or stats.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Load SMS templates when SMS tab is accessed
  useEffect(() => {
    if (activeTab === 'sms-templates' && smsTemplates.length === 0) {
      loadSmsTemplates();
    }
  }, [activeTab]);

  // SMS Templates functions
  const loadSmsTemplates = async () => {
    setSmsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sms-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmsTemplates(response.data);
    } catch (err) {
      console.error('Error loading SMS templates:', err);
      toast.error('Failed to load SMS templates');
    } finally {
      setSmsLoading(false);
    }
  };

  const openSmsTemplateModal = (template = null) => {
    if (template) {
      setSelectedSmsTemplate(template);
      setSmsTemplateForm({
        name: template.name,
        title: template.title,
        message: template.message,
        isActive: template.isActive,
        order: template.order
      });
    } else {
      setSelectedSmsTemplate(null);
      setSmsTemplateForm({
        name: '',
        title: '',
        message: '',
        isActive: true,
        order: 0
      });
    }
    setShowSmsTemplateModal(true);
  };

  const saveSmsTemplate = async () => {
    if (!smsTemplateForm.name || !smsTemplateForm.title || !smsTemplateForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingSmsTemplate(true);
    try {
      if (selectedSmsTemplate) {
        // Update existing template
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/sms-templates/${selectedSmsTemplate._id}`,
          smsTemplateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('SMS template updated successfully!');
      } else {
        // Create new template
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/sms-templates`,
          smsTemplateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('SMS template created successfully!');
      }
      
      setShowSmsTemplateModal(false);
      loadSmsTemplates(); // Reload templates
    } catch (err) {
      console.error('Error saving SMS template:', err);
      toast.error(err.response?.data?.error || 'Failed to save SMS template');
    } finally {
      setSavingSmsTemplate(false);
    }
  };

  const deleteSmsTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this SMS template?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/sms-templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SMS template deleted successfully!');
      loadSmsTemplates(); // Reload templates
    } catch (err) {
      console.error('Error deleting SMS template:', err);
      toast.error('Failed to delete SMS template');
    }
  };

  const toggleSmsTemplateStatus = async (templateId) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/sms-templates/${templateId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('SMS template status updated!');
      loadSmsTemplates(); // Reload templates
    } catch (err) {
      console.error('Error toggling SMS template status:', err);
      toast.error('Failed to update SMS template status');
    }
  };

  // Filter + sort
  const displayed = useMemo(() => {
    let filtered = users.filter(u => {
      // Search filter
      const matchesSearch = [u.name, u.email, u.companyName || '']
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      // Status filter
      let matchesStatus = true;
      if (statusFilter !== 'all') {
        const userStatus = (u.subscriptionStatus || 'inactive').toLowerCase();
        matchesStatus = userStatus === statusFilter;
      }
      
      return matchesSearch && matchesStatus;
    });
    
    const { key, direction } = sortConfig;
    filtered.sort((a, b) => {
      const va = (a[key] || '').toString().toLowerCase();
      const vb = (b[key] || '').toString().toLowerCase();
      if (va < vb) return direction === 'asc' ? -1 : 1;
      if (va > vb) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [users, searchTerm, sortConfig, statusFilter]);

  const requestSort = key =>
    setSortConfig(cfg => ({
      key,
      direction: cfg.key === key && cfg.direction === 'asc' ? 'desc' : 'asc'
    }));
  const getIcon = key =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : null;

  if (loading) return <Spinner animation="border" className="m-5" />;
  if (error)   return <Alert variant="danger">{error}</Alert>;

  // Actions dropdown
  const ActionsDropdown = ({ user }) => (
    <Dropdown>
      <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => openEdit(user)}>Edit</Dropdown.Item>
        <Dropdown.Item onClick={() => setDeleteUser(user)}>Delete</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  // Helper function to get status badge styling
  const getStatusBadge = (status) => {
    const statusLower = (status || 'inactive').toLowerCase();
    const badgeColors = {
      'active': 'success',
      'suspended': 'danger',
      'pending': 'warning',
      'inactive': 'secondary'
    };
    const badgeLabels = {
      'active': 'Active',
      'suspended': 'Suspended',
      'pending': 'Pending',
      'inactive': 'Inactive'
    };
    
    return (
      <Badge bg={badgeColors[statusLower] || 'secondary'}>
        {badgeLabels[statusLower] || status || 'Inactive'}
      </Badge>
    );
  };

  // Desktop table with stats columns
  const DesktopTable = () => (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th onClick={() => requestSort('name')}>Name {getIcon('name')}</th>
          <th onClick={() => requestSort('email')}>Email {getIcon('email')}</th>
          <th onClick={() => requestSort('subscriptionStatus')}>Status {getIcon('subscriptionStatus')}</th>
          <th>Accepted</th>
          <th>Declined</th>
          <th>Passed</th>
          <th>Completed</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {displayed.map(u => {
          const s = stats[u.email] || { accepted:0, declined:0, passed:0, completed:0 };
          return (
            <tr key={u._id}>
              <td>{u.name}</td>
              <td>{u.email}</td>
              <td>{getStatusBadge(u.subscriptionStatus)}</td>
              <td>{s.accepted}</td>
              <td>{s.declined}</td>
              <td>{s.passed}</td>
              <td>{s.completed}</td>
              <td><ActionsDropdown user={u} /></td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  // Mobile accordion with stats
  const MobileList = () => (
    <Accordion flush>
      {displayed.map((u, i) => {
        const s = stats[u.email] || { accepted:0, declined:0, passed:0, completed:0 };
        return (
          <Accordion.Item eventKey={String(i)} key={u._id}>
            <Accordion.Header>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  {u.name} — ✔{s.accepted} ✖{s.declined} ↔{s.passed} ✅{s.completed}
                </div>
                {getStatusBadge(u.subscriptionStatus)}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <p><strong>Email:</strong> {u.email}</p>
              <p><strong>Status:</strong> 
                <span className="ms-2">{getStatusBadge(u.subscriptionStatus)}</span>
              </p>
              <ActionsDropdown user={u} />
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );

  // SMS Templates Table
  const SmsTemplatesTable = () => (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th>Name</th>
          <th>Title</th>
          <th>Message Preview</th>
          <th>Status</th>
          <th>Order</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {smsTemplates.map(template => (
          <tr key={template._id}>
            <td>{template.name}</td>
            <td>{template.title}</td>
            <td>
              <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {template.message}
              </div>
            </td>
            <td>
              <Badge bg={template.isActive ? 'success' : 'secondary'}>
                {template.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </td>
            <td>{template.order}</td>
            <td>
              <Dropdown>
                <Dropdown.Toggle size="sm">Actions</Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => openSmsTemplateModal(template)}>Edit</Dropdown.Item>
                  <Dropdown.Item onClick={() => toggleSmsTemplateStatus(template._id)}>
                    {template.isActive ? 'Deactivate' : 'Activate'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => deleteSmsTemplate(template._id)} className="text-danger">
                    Delete
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  // SMS Templates Mobile List
  const SmsTemplatesMobileList = () => (
    <Accordion flush>
      {smsTemplates.map((template, i) => (
        <Accordion.Item eventKey={String(i)} key={template._id}>
          <Accordion.Header>
            <div>
              <div><strong>{template.name}</strong></div>
              <small>{template.title}</small>
              <Badge bg={template.isActive ? 'success' : 'secondary'} className="ms-2">
                {template.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <p><strong>Message:</strong> {template.message}</p>
            <p><strong>Order:</strong> {template.order}</p>
            <div className="d-flex gap-2">
              <Button size="sm" onClick={() => openSmsTemplateModal(template)}>Edit</Button>
              <Button 
                size="sm" 
                variant={template.isActive ? 'warning' : 'success'}
                onClick={() => toggleSmsTemplateStatus(template._id)}
              >
                {template.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button 
                size="sm" 
                variant="danger"
                onClick={() => deleteSmsTemplate(template._id)}
              >
                Delete
              </Button>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  // Handlers for Edit and Delete
  const openEdit = user => {
    setEditUser(user);
  };

  const handleProfileUpdated = updatedUser => {
    if (!updatedUser) return;
    setUsers(prev => prev.map(u => (u._id === updatedUser._id ? { ...u, ...updatedUser } : u)));
    setStats(prev => {
      if (!editUser) return prev;
      const currentStats = { ...prev };
      if (editUser.email && editUser.email !== updatedUser.email) {
        const snapshot = currentStats[editUser.email];
        delete currentStats[editUser.email];
        if (snapshot) {
          currentStats[updatedUser.email] = snapshot;
        }
      }
      return currentStats;
    });
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${deleteUser._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(us => us.filter(x => x._id !== deleteUser._id));
      setDeleteUser(null);
    } catch {
      alert('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-center" />
      <div className="container mt-4">
        <h2>Drivers Admin</h2>
        
        {/* Tab Navigation */}
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'users'} 
              onClick={() => setActiveTab('users')}
            >
              👥 Manage Users
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'sms-templates'} 
              onClick={() => setActiveTab('sms-templates')}
            >
              📱 SMS Templates
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
            <div className="d-flex gap-3 mb-3 flex-wrap align-items-end">
              <div className="position-relative" style={{ flex: '1 1 300px', maxWidth: 500 }}>
                <Form.Label>Search Drivers</Form.Label>
                <Form.Control
                  placeholder="Search by name, email or company…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  size="sm"
                  style={{ paddingRight: '2rem' }}
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                    tabIndex={0}
                    style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none', marginTop: '0.6rem' }}
                  >
                    ×
                  </Button>
                )}
              </div>
              <div style={{ minWidth: 180 }}>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  size="sm"
                >
                  <option value="all">All Drivers</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </div>
            </div>
            {displayed.length === 0
              ? <p>No users found.</p>
              : isMobile
                ? <MobileList/>
                : <DesktopTable/>
            }
          </>
        )}

        {/* SMS Templates Tab Content */}
        {activeTab === 'sms-templates' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>SMS Message Templates</h4>
              <Button onClick={() => openSmsTemplateModal()}>
                ➕ Add New Template
              </Button>
            </div>
            
            {smsLoading ? (
              <Spinner animation="border" className="m-5" />
            ) : smsTemplates.length === 0 ? (
              <Card>
                <Card.Body className="text-center">
                  <p>No SMS templates found.</p>
                  <Button onClick={() => openSmsTemplateModal()}>
                    Create Your First Template
                  </Button>
                </Card.Body>
              </Card>
            ) : isMobile ? (
              <SmsTemplatesMobileList />
            ) : (
              <SmsTemplatesTable />
            )}
          </>
        )}
      </div>

      <DriverProfileAdminModal
        show={!!editUser}
        user={editUser}
        onHide={() => setEditUser(null)}
        onSaved={handleProfileUpdated}
      />

      {/* Delete User Modal */}
      <Modal show={!!deleteUser} onHide={()=>setDeleteUser(null)} centered>
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete user <strong>{deleteUser?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setDeleteUser(null)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting?'Deleting…':'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SMS Template Modal */}
      <Modal show={showSmsTemplateModal} onHide={() => setShowSmsTemplateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSmsTemplate ? 'Edit SMS Template' : 'Create New SMS Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Template Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., booking_confirmation, pickup_reminder"
                value={smsTemplateForm.name}
                onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              />
              <Form.Text className="text-muted">
                Unique identifier for this template (no spaces, use underscores)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Display Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Booking Confirmation, Pickup Reminder"
                value={smsTemplateForm.title}
                onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, title: e.target.value }))}
              />
              <Form.Text className="text-muted">
                Title that drivers will see when selecting templates
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter your SMS message here..."
                value={smsTemplateForm.message}
                onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, message: e.target.value }))}
              />
              <Form.Text className="text-muted">
                You can use variables like {'{customerName}'}, {'{pickupTime}'}, {'{pickupLocation}'}, {'{dropoffLocation}'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                placeholder="0"
                value={smsTemplateForm.order}
                onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
              <Form.Text className="text-muted">
                Lower numbers appear first in the list
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active (available to drivers)"
                checked={smsTemplateForm.isActive}
                onChange={(e) => setSmsTemplateForm(prev => ({ ...prev, isActive: e.target.checked }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSmsTemplateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveSmsTemplate} disabled={savingSmsTemplate}>
            {savingSmsTemplate ? 'Saving...' : (selectedSmsTemplate ? 'Update Template' : 'Create Template')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
